import { queryOptions, useQuery, useQueryClient, useMutation, type UseQueryResult } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { useCallback } from 'react'
import { v1Client, useApi } from '@renderer/chat'
import { useGlobalStore } from '@highlight/ipc/react'

export const liveContextKeys = {
  environment: (environmentName: string) => ['live-context', environmentName] as const,
  environments: () => ['live-context-environments'] as const,
  history: (environmentName: string) => ['live-context-history', environmentName] as const,
  byId: (id: string | null) => ['live-context-by-id', id] as const,
}

export type LiveContext = {
  id: string
  userId: string
  environmentName: string
  context?: string
  prompt?: string | undefined
  updateSummary?: string | undefined
  contextPointers: Array<{ type: string; id: string }>
  createdAt: string
  updatedAt: string
  metadata?: Record<string, unknown> | null
  llmMessageId?: string
  conversationId?: string
}

export type LiveContextObject = {
  context: string
  updateSummary: string
  createdAt: string
  prompt: string
  audioNoteCount: number
  audioNoteIds: Array<string>
}

/**
 * Hook for editing a live context's content.
 * Call PATCH /v1/live-context/{id} with the updated context.
 */
export function useEditLiveContext() {
  const { patch } = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['edit-live-context'],
    mutationFn: async ({ liveContextId, context }: { liveContextId: string; context: string }) => {
      const resp = await patch(`live-context/${liveContextId}`, JSON.stringify({ context }), {
        version: 'v1',
        headers: { 'content-type': 'application/json' },
      })
      if (!resp.ok) {
        throw new Error(`Failed to edit live context: ${resp.status}, ${resp.statusText}`)
      }

      const json = await resp.json()
      if (!json.success) {
        throw new Error('Unexpected error editing live context')
      }

      return json.data as LiveContext
    },
    onSuccess: (data, variables) => {
      // Reflect the save before the refetch lands so editors don't see the pre-save row as a newer version.
      queryClient.setQueryData<LiveContext | null>(liveContextKeys.environment(data.environmentName), (current) =>
        current?.id === data.id ? { ...current, ...data, context: variables.context } : current,
      )
      queryClient.invalidateQueries({ queryKey: liveContextKeys.environment(data.environmentName) })
    },
  })
}

export function liveContextQueryOptions(environmentName: string, getAuthToken: () => Promise<string>) {
  return queryOptions({
    queryKey: liveContextKeys.environment(environmentName),
    queryFn: async (): Promise<LiveContext | null> => {
      if (!environmentName) {
        return null
      }
      const response = await v1Client.GET('/live-context', {
        params: { query: { environmentName } },
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      })
      if (response.error) {
        throw new Error('Failed to fetch live context')
      }
      // Without `list`, the endpoint returns a single row (or null), not the list shape.
      return response.data.data as LiveContext | null
    },
    enabled: !!environmentName,
  })
}

/** Latest live context for an environment. Disabled while `environmentName` is empty. */
export function useLiveContextQuery(environmentName: string = 'Default'): UseQueryResult<LiveContext | null> {
  const { getAuthToken } = useAuth()
  return useQuery(liveContextQueryOptions(environmentName, getAuthToken))
}

export function useGenerateLiveContext(environmentName: string): {
  generateLiveContext: (prompt?: string, metadata?: Record<string, unknown>) => Promise<LiveContext>
  isGenerating: boolean
} {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const [regenerateFromScratch] = useGlobalStore<boolean>('settings', 'liveContext.regenerateFromScratch', false)

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      prompt,
      metadata,
      regenerate,
    }: {
      prompt: string
      metadata: Record<string, unknown>
      regenerate: boolean
    }): Promise<LiveContext> => {
      const response = await v1Client.POST('/live-context', {
        body: { environmentName, prompt, metadata, regenerate },
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      })
      if (response.error) {
        throw new Error('Failed to generate live context')
      }
      return response.data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: liveContextKeys.environment(environmentName) })
    },
  })

  const generateLiveContext = useCallback(
    (prompt: string = '', metadata: Record<string, unknown> = {}) =>
      mutateAsync({ prompt, metadata, regenerate: regenerateFromScratch }),
    [mutateAsync, regenerateFromScratch],
  )

  return { generateLiveContext, isGenerating: isPending }
}
